// filepath: apps/web/src/renderers/__shared__/useConflictResolver.tsx
//
// FR-UI-CORE-6 cross-renderer conflict-resolution hook.
//
// Why a shared utility:
//   The 409 write-conflict UX is needed by BOTH the form renderer
//   (FR-UI-FORM-1 entity writes) AND the task renderer (FR-UI-TASK-1
//   human-task completion per ARCHITECTURE_WEB.md §5). A shared
//   provider + hook mirrors the same architectural pattern that
//   `serverShape.ts` (FR-UI-CORE-4 AC-4) established for renderer
//   discipline: the contract lives once, renderers import it.
//
// CONTRACT (FR-UI-CORE-6):
//   - `HttpConflictError` arrives from any write mutation that the
//     server rejects on stale `row_version` (FR-ENT-8).
//   - `show(err, userRecord)` opens the conflict-resolution modal with
//     both the server's `currentRecord` (read-only diff source) and
//     the user's pending `userRecord` (read-only diff source).
//   - `resolve('reapply' | 'refetch' | 'discard')` invokes the
//     action chosen by the user; the renderer decides what to do with
//     the result.
//   - `dismiss()` closes the modal without firing any of the three
//     resolution paths — the user has explicitly walked away (AC-4:
//     no silent retry with the stale `row_version`).
//
// INV-2 / FR-AUTH-7:
//   The 'reapply' merge MUST drop any field the server stripped from
//   `currentRecord` — it must NEVER re-introduce such a field from
//   `userRecord`. The merged payload's `row_version` MUST come from
//   `currentRowVersion` (the server's fresh value), not the stale
//   `row_version` the user originally submitted.
//
// Provider/consumer shape:
//   The hook is implemented as a tiny React context provider so the
//   hook can be called from any descendant (FormRenderer, future
//   TaskInbox / TaskComplete) without prop-drilling. The provider is
//   mounted near the renderers in apps/web; the hook falls back to a
//   no-op state when called outside a provider (mirrors
//   useTenant's defensive fallback for tests).

import React from "react";
import { HttpConflictError } from "@/definitions/types";

/** The three resolution actions the modal offers. */
export type ConflictAction = "reapply" | "refetch" | "discard";

/** The user-visible payload of the conflict modal. */
export interface ConflictPayload {
  /** The server-supplied current record. May be `null` if the server
   *  omitted it — the UI degrades gracefully (no diff context shown). */
  currentRecord: Record<string, unknown> | null;
  /** The server-supplied fresh row_version. May be `null` if the
   *  server omitted it — the UI uses "Refetch latest" semantics. */
  currentRowVersion: number | null;
  /** The user's pending edit at the time of the 409. Captured by
   *  `show()` so the modal can render it read-only for diff context. */
  userRecord: Record<string, unknown>;
  /** The original 409 error, preserved for diagnostics. */
  error: HttpConflictError;
}

/**
 * The shape of the result returned to the renderer after the user
 * picks a resolution action.
 */
export interface ConflictResolutionResult {
  action: ConflictAction;
  /** The merged payload for 'reapply'. `null` for 'refetch' and
   *  'discard' — the renderer is responsible for invalidating the
   *  cache on 'refetch' and resetting local state on 'discard'. */
  mergedPayload: Record<string, unknown> | null;
  /** The fresh `row_version` to use on the next write. `null` if the
   *  server didn't supply one (the renderer should fall back to a
   *  full refetch in that case). */
  currentRowVersion: number | null;
}

export interface UseConflictResolverResult {
  /** The current conflict payload, or `null` if no conflict is active. */
  conflict: ConflictPayload | null;
  /** Open the conflict modal. `userRecord` is the user's pending edit
   *  at the time of the 409 (captured by the caller's mutation hook). */
  show: (err: HttpConflictError, userRecord: Record<string, unknown>) => void;
  /** Resolve the conflict with one of three actions. */
  resolve: (action: ConflictAction) => ConflictResolutionResult | null;
  /** Dismiss the conflict modal without picking an action. */
  dismiss: () => void;
}

const ConflictContext = React.createContext<UseConflictResolverResult | null>(
  null,
);

/**
 * Provider. Mount once near the app root (or near the renderer
 * subtree). Renderers call `useConflictResolver()` to get the
 * current conflict state and the action methods.
 */
export function ConflictResolverProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [conflict, setConflict] = React.useState<ConflictPayload | null>(null);
  const [resolveCb, setResolveCb] = React.useState<
    ((result: ConflictResolutionResult) => void) | null
  >(null);

  const show = React.useCallback(
    (err: HttpConflictError, userRecord: Record<string, unknown>) => {
      const payload: ConflictPayload = {
        currentRecord: err.currentRecord,
        currentRowVersion: err.currentRowVersion,
        userRecord,
        error: err,
      };
      setConflict(payload);
      // Auto-arm a one-shot resolver callback so `resolve()` can return
      // synchronously to the caller. This is what the FormRenderer's
      // mutation hook awaits when wiring the "reapply" re-PUT.
      setResolveCb(() => (result: ConflictResolutionResult) => {
        setConflict(null);
        setResolveCb(null);
        return result;
      });
    },
    [],
  );

  const dismiss = React.useCallback(() => {
    setConflict(null);
    setResolveCb(null);
  }, []);

  const resolve = React.useCallback(
    (action: ConflictAction): ConflictResolutionResult | null => {
      if (!conflict) return null;
      let result: ConflictResolutionResult;
      if (action === "reapply") {
        result = {
          action: "reapply",
          mergedPayload: mergeReapplyPayload(
            conflict.currentRecord,
            conflict.userRecord,
          ),
          currentRowVersion: conflict.currentRowVersion,
        };
      } else if (action === "refetch") {
        result = {
          action: "refetch",
          mergedPayload: null,
          currentRowVersion: conflict.currentRowVersion,
        };
      } else {
        result = {
          action: "discard",
          mergedPayload: null,
          currentRowVersion: conflict.currentRowVersion,
        };
      }
      // Notify any awaiter, then clear state.
      resolveCb?.(result);
      return result;
    },
    [conflict, resolveCb],
  );

  const value = React.useMemo<UseConflictResolverResult>(
    () => ({ conflict, show, resolve, dismiss }),
    [conflict, show, resolve, dismiss],
  );

  return (
    <ConflictContext.Provider value={value}>
      {children}
    </ConflictContext.Provider>
  );
}

/**
 * Hook. Returns the conflict-resolver context. When called outside a
 * provider (e.g. from a unit test that renders only the leaf), it
 * returns a no-op stub — `conflict` is always null, `show` is a no-op,
 * `resolve` returns null, `dismiss` is a no-op.
 */
export function useConflictResolver(): UseConflictResolverResult {
  const ctx = React.useContext(ConflictContext);
  if (ctx === null) {
    return NOOP_RESOLVER;
  }
  return ctx;
}

const NOOP_RESOLVER: UseConflictResolverResult = {
  conflict: null,
  show: () => {
    /* noop — outside provider */
  },
  resolve: () => null,
  dismiss: () => {
    /* noop */
  },
};

/**
 * INV-2 merge for the 'reapply' action.
 *
 * Rules (FR-UI-CORE-6 AC-3 + INV-2 / FR-AUTH-7):
 *   1. Start with `currentRecord` as the authoritative shape — every
 *      field the server returned is addressable; every field absent
 *      from `currentRecord` is NOT (the server has stripped it; the
 *      renderer MUST NOT re-introduce it from `userRecord`).
 *   2. Overlay `userRecord` values on top, but ONLY for keys already
 *      present in `currentRecord`. Keys in `userRecord` that are NOT
 *      in `currentRecord` are dropped silently.
 *   3. The merged payload's `row_version` is the SERVER's fresh value
 *      (`currentRowVersion`), not the stale `row_version` the user
 *      originally submitted. The caller passes `currentRowVersion` in
 *      separately and is responsible for carrying it on the next
 *      write; this function does NOT mutate `row_version` so the
 *      caller can build the request body deterministically.
 *
 * When `currentRecord` is `null` (server omitted it) the merge is
 * treated as "the user must refetch" — we return the `userRecord`
 * verbatim, but the renderer should detect this case (via
 * `currentRecord === null`) and route the user to "Refetch latest"
 * rather than retry the write with a server-authoritative shape it
 * cannot compute.
 */
export function mergeReapplyPayload(
  currentRecord: Record<string, unknown> | null,
  userRecord: Record<string, unknown>,
): Record<string, unknown> {
  if (currentRecord === null) {
    // Server did not supply a current record body. The renderer
    // should treat this as a graceful-degradation signal: route the
    // user to "Refetch latest", not "Re-apply". We still return the
    // user record so the hook contract is symmetric, but the caller
    // is expected to check `currentRecord === null` first.
    return { ...userRecord };
  }
  const merged: Record<string, unknown> = { ...currentRecord };
  for (const key of Object.keys(userRecord)) {
    // INV-2: never re-introduce a server-stripped field.
    if (!Object.prototype.hasOwnProperty.call(currentRecord, key)) continue;
    merged[key] = userRecord[key];
  }
  return merged;
}
